import './styles/AgentLockerFixStyle.css';
import { useCallback, useRef, useEffect, useState } from 'react';
import { languageMap } from '../../maps/languageMap';
import { useActions, useTypedSelector } from '../../hooks';
import { APIResponseStatusType, navigationPages } from '../../models/types';
import {
  LockerColumn,
  NavigationButton,
  ToastType,
  TopNav,
  TopNavToastMessage,
} from '../common';
import { useHistory } from 'react-router-dom';
import {
  LockerColumnStatuses,
  LockerColumnTypes,
  LockerPosition,
  LockerPositionStatus,
  LockerPositionType,
  PackageCandidate,
} from '../../models/microservices';
import Spinner from 'react-bootstrap/Spinner';
import { FaCheckCircle } from 'react-icons/fa';
import { MdCancel } from 'react-icons/md';
import { FaCircle } from 'react-icons/fa';
import { Lock } from '../../devices/locker/interface/Locker/Lock';
import {
  IncidentRequestPOST,
  IncidentResponsePOST,
  LockerContentRequestGET,
  LockerContentResponseGET,
  LockerPositionRequestPUT,
  LockerPositionResponsePUT,
} from '../../models/cruds';
import { apiErrorHandler, apiGET, apiPOST, apiPUT } from '../../restApi';

const LOCKER_OPEN_TIMEOUT = 5;
const LOCKER_CLOSE_TIMEOUT = 15;
const LOCKER_STATUS_FREE = 2;
const LOCKER_STATUS_DAMAGED = 1;
const LOCKER_FIX_OCCUPIED_COMPARTMENT_INCIDENT_ID = 11;
const PRIMARY_COLUMN_COMPARTMENT_LENGTH = 8;
const PRIMARY_MASTER_COMPARTMENT_INDEX = 1;
const PRIMARY_MASTER_COMPARTMENT = {
  lockerPositionType: {
    name: LockerColumnTypes.SCREEN,
  } as LockerPositionType,
  lockerPositionStatus: {
    name: LockerColumnStatuses.BLOCKED,
  } as LockerPositionStatus,
} as LockerPosition;
type Feedback = {
  message: string;
  type: ToastType;
};
const AgentLockerFix: React.FC = () => {
  // #region States
  let history = useHistory();
  const [testing, setTesting] = useState(false);
  const [loading, setLoading] = useState(false);
  const currentLockRef = useRef<Lock>();
  const lockOpenedRef = useRef<boolean>();
  const testingRef = useRef<boolean>();
  const lockerPositionRef = useRef<LockerPosition>();
  const packageCandidateRef = useRef<PackageCandidate>();
  const closeTimerRef = useRef<NodeJS.Timeout | undefined>();
  const openTimerRef = useRef<NodeJS.Timeout | undefined>();
  const [currentStep, setCurrentStep] = useState(0);
  const [currentLock, setCurrentLock] = useState<Lock>();
  const [lockOpened, setLockOpened] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>({} as Feedback);
  const [instructions, setInstructions] = useState('');
  const language = useTypedSelector(({ session }) => {
    return session.language;
  });
  const token = useTypedSelector(({ session }) => {
    return session.dhlApiToken;
  });
  const lockerContent = useTypedSelector(({ locker }) => {
    return locker.lockerContent;
  });
  const lockerRackId = useTypedSelector(({ locker }) => {
    return locker.lockerRackId;
  });
  const currentLockerPosition = useTypedSelector(({ locker }) => {
    return locker.currentLockerPosition;
  });
  const currentPackageCandidate = useTypedSelector(({ locker }) => {
    return locker.currentPackageCandidate;
  });
  const locker = useTypedSelector(({ devices }) => {
    return devices.locker;
  });
  currentLockRef.current = currentLock;
  lockOpenedRef.current = lockOpened;
  testingRef.current = testing;
  lockerPositionRef.current = currentLockerPosition;
  packageCandidateRef.current = currentPackageCandidate;
  // #endregion States
  const { updateLockerContent, updateCurrentLockerPosition } = useActions();
  //On unloading
  useEffect(
    () => () => {
      const removeLockerEventListeners = () => {
        locker?.removeEventListener(
          'lockOpened',
          onLockOpened as EventListener
        );
        locker?.removeEventListener(
          'lockClosed',
          onLockClosed as EventListener
        );
      };
      removeLockerEventListeners();
    },
    []
  );
  useEffect(() => {
    if (!currentLockerPosition.id) {
      setInstructions(
        languageMap.agent_locker_fix.select_compartment_instructions[language]
      );
    }
  }, [language, currentLockerPosition]);
  useEffect(() => {
    if (locker && currentLock?.sid && currentLock?.cid) {
      console.log(' el current lock', currentLock);
      locker.openLockAsync(currentLock);
    }
  }, [locker, currentLock]);
  // #region Locker callbacks
  const onLockOpened = useCallback(
    (event: CustomEvent) => {
      console.log(
        event,
        lockOpenedRef.current,
        testingRef.current,
        lockOpenedRef.current || !testingRef.current,
        currentLockRef.current,
        event.detail as Lock
      );

      if (lockOpenedRef.current || !testingRef.current) {
        clearTimeout(openTimerRef.current);
        return;
      }
      if (
        (event.detail as Lock).sid === currentLockRef.current?.sid &&
        (event.detail as Lock).cid === currentLockRef.current?.cid
      ) {
        console.log(
          'lock Opened',
          event.detail,
          currentLockRef.current,
          lockOpenedRef.current,
          testingRef.current
        );
        setLockOpened(true);
        clearTimeout(openTimerRef.current);
        locker?.addEventListener('lockClosed', onLockClosed as EventListener);
        setCurrentStep(1);
        setInstructions(
          languageMap.agent_locker_fix.close_compartment_instructions[language]
        );
        closeTimerRef.current = setTimeout(() => {
          console.log('Timeout close');
          if (!lockOpenedRef.current) {
            locker?.removeEventListener(
              'lockOpened',
              onLockOpened as EventListener
            );
            locker?.removeEventListener(
              'lockClosed',
              onLockClosed as EventListener
            );
            setFeedback({
              message:
                languageMap.agent_locker_fix.feedback_test_failed[language],
              type: ToastType.ERROR,
            });
            setTesting(false);
          }
        }, LOCKER_CLOSE_TIMEOUT * 1000);
      }
    },
    [language]
  );
  const onLockClosed = useCallback(
    (event: CustomEvent) => {
      console.log(
        'lock Closed',
        event.detail,
        currentLockRef.current,
        lockOpenedRef.current,
        testingRef.current,
        currentLockerPosition,
        lockerPositionRef.current
      );
      if (!lockOpenedRef.current || !testingRef.current) {
        return;
      }
      if (
        (event.detail as Lock).sid === currentLockRef.current?.sid &&
        (event.detail as Lock).cid === currentLockRef.current?.cid
      ) {
        clearTimeout(closeTimerRef.current);
        setLockOpened(false);
        locker?.removeEventListener(
          'lockOpened',
          onLockOpened as EventListener
        );
        locker?.removeEventListener(
          'lockClosed',
          onLockClosed as EventListener
        );
        setCurrentStep(2);
        if (
          lockerPositionRef.current?.lockerPositionStatus?.id ===
          LOCKER_STATUS_DAMAGED
        ) {
          setInstructions(
            languageMap.agent_locker_fix.updating_feedback[language]
          );
          if (
            lockerPositionRef.current?.packageCandidate?.awbNumber &&
            lockerPositionRef.current?.packageCandidate.awbNumber !== ''
          ) {
            apiFixOccupiedDamagedCompartment();
          } else {
            apiFixDamagedCompartment();
          }
        } else {
          resetCompartment();
        }
      }
    },
    [language]
  );
  // #endregion Locker callbacks
  // #region API Calls
  const getlockerContent = async (): Promise<boolean> => {
    setLoading(true);
    let request = new LockerContentRequestGET();
    let response: LockerContentResponseGET = await apiGET(
      request.buildURL(),
      request.generateConfig(token)
    );
    let errorMsg = '';
    try {
      if (response.status === APIResponseStatusType.SUCCESS) {
        if (response.data.lockerRack) {
          let content = response.data.lockerRack;
          if (content.lockerPositions && content.lockerPositions.length !== 0) {
            updateLockerContent(content.lockerPositions);
            setLoading(false);
            return true;
          } else {
            setFeedback({
              message:
                languageMap.agent_locker_fix.feedback__content_update_failed[
                  language
                ],
              type: ToastType.ERROR,
            });
            setLoading(false);
            return false;
          }
        } else {
          errorMsg = 'Unauthorized';
        }
      } else {
        errorMsg = apiErrorHandler(response);
      }
    } catch (err: any) {
      errorMsg = err.toString();
    }
    console.log(errorMsg);
    setFeedback({
      message:
        languageMap.agent_locker_fix.feedback_content_update_failed[language],
      type: ToastType.ERROR,
    });
    setLoading(false);
    return false;
  };
  const apiFixOccupiedDamagedCompartment = async () => {
    let packageCandidateId = currentPackageCandidate.id ?? 0;
    let lockerPositionId = packageCandidateRef.current?.id ?? 0;
    let request = new IncidentRequestPOST();
    let response: IncidentResponsePOST = await apiPOST(
      request.buildURL(),
      request.generateContent(
        packageCandidateId,
        lockerPositionId,
        LOCKER_FIX_OCCUPIED_COMPARTMENT_INCIDENT_ID
      ),
      request.generateConfig(token)
    );
    let errorMsg = '';
    try {
      if (response.status === APIResponseStatusType.SUCCESS) {
        resetCompartment();
        getlockerContent();
        return;
      } else {
        errorMsg = apiErrorHandler(response);
      }
    } catch (err: any) {
      errorMsg = err.toString();
    }
    console.log(errorMsg);
    setFeedback({
      message:
        languageMap.agent_locker_fix.feedback_api_update_failed[language],
      type: ToastType.ERROR,
    });
    setTesting(false);
    return false;
  };
  const apiFixDamagedCompartment = async () => {
    let lockerPositionId = lockerPositionRef.current?.id ?? 0;
    let rackId = lockerRackId;
    let lockerRow = lockerPositionRef.current?.row ?? '';
    let lockerColumn = lockerPositionRef.current?.column ?? '';
    let lockerPositionTypeId =
      lockerPositionRef.current?.lockerPositionType.id ?? 0;
    let lockerPositionStatusId = LOCKER_STATUS_FREE;
    let cid = lockerPositionRef.current?.extraFields.cid ?? '';
    let sid = lockerPositionRef.current?.extraFields.sid ?? '';
    let isActive = true;
    let request = new LockerPositionRequestPUT();
    let response: LockerPositionResponsePUT = await apiPUT(
      request.buildURL(lockerPositionId),
      request.generateContent(
        lockerPositionId,
        rackId,
        lockerRow,
        lockerColumn,
        lockerPositionTypeId,
        lockerPositionStatusId,
        cid,
        sid,
        isActive
      ),
      request.generateConfig(token)
    );
    let errorMsg = '';
    try {
      if (response.status === APIResponseStatusType.SUCCESS) {
        resetCompartment();
        getlockerContent();
        return;
      } else {
        errorMsg = apiErrorHandler(response);
      }
    } catch (err: any) {
      errorMsg = err.toString();
    }
    console.log(errorMsg);
    setFeedback({
      message:
        languageMap.agent_locker_fix.feedback_api_update_failed[language],
      type: ToastType.ERROR,
    });
    setTesting(false);
    return false;
  };
  const apiTestDamageCompartment = async () => {
    let lockerPositionId = currentLockerPosition.id;
    let rackId = lockerRackId;
    let lockerRow = currentLockerPosition.row;
    let lockerColumn = currentLockerPosition.column;
    let lockerPositionTypeId = currentLockerPosition.lockerPositionType?.id;
    let lockerPositionStatusId = LOCKER_STATUS_DAMAGED;
    let cid = currentLockerPosition.extraFields.cid;
    let sid = currentLockerPosition.extraFields.sid;
    let isActive = true;
    let request = new LockerPositionRequestPUT();
    let response: LockerPositionResponsePUT = await apiPUT(
      request.buildURL(lockerPositionId),
      request.generateContent(
        lockerPositionId,
        rackId,
        lockerRow,
        lockerColumn,
        lockerPositionTypeId,
        lockerPositionStatusId,
        cid,
        sid,
        isActive
      ),
      request.generateConfig(token)
    );
    let errorMsg = '';
    try {
      if (response.status === APIResponseStatusType.SUCCESS) {
        resetCompartment();
        getlockerContent();
        return;
      } else {
        errorMsg = apiErrorHandler(response);
      }
    } catch (err: any) {
      errorMsg = err.toString();
    }
    console.log(errorMsg);
    setFeedback({
      message:
        languageMap.agent_locker_fix.feedback_api_update_failed[language],
      type: ToastType.ERROR,
    });
    setTesting(false);
    return false;
  };
  // #endregion API Calls
  const resetCompartment = () => {
    console.log('Restting compartment');
    updateCurrentLockerPosition({} as LockerPosition);
    setCurrentLock({} as Lock);
    setFeedback({
      message:
        languageMap.agent_locker_fix.feedback_compartment_updated[language],
      type: ToastType.SUCCESS,
    });
    setInstructions(
      languageMap.agent_locker_fix.feedback_compartment_updated[language]
    );
    setTesting(false);
  };
  const testCompartment = async () => {
    locker?.removeEventListener('lockOpened', onLockOpened as EventListener);
    locker?.removeEventListener('lockClosed', onLockClosed as EventListener);
    locker?.addEventListener('lockOpened', onLockOpened as EventListener);
    setTesting(true);
    setInstructions(languageMap.agent_locker_fix.opening_feedback[language]);
    // Trigger an open lock async by setting the current locker position
    // setCurrentLock(new Lock('2222BBBG0749', '1'));
    setCurrentLock(
      new Lock(
        currentLockerPosition.extraFields.sid,
        currentLockerPosition.extraFields.cid
      )
    );
    openTimerRef.current = setTimeout(() => {
      console.log('Timeout open');
      if (!lockOpenedRef.current) {
        locker?.removeEventListener(
          'lockOpened',
          onLockOpened as EventListener
        );
        locker?.removeEventListener(
          'lockClosed',
          onLockClosed as EventListener
        );
        setInstructions(
          languageMap.agent_locker_fix.pre_test_instructions[language]
        );
        setFeedback({
          message: languageMap.agent_locker_fix.feedback_test_failed[language],
          type: ToastType.ERROR,
        });
        setTesting(false);
      }
    }, LOCKER_OPEN_TIMEOUT * 1000);
  };
  const renderLocker = () => {
    if (loading) {
      return (
        <div className="spinner-container">
          <Spinner className="spinner" animation="grow" variant="danger" />
        </div>
      );
    }
    const groupedLockers: { [key: string]: LockerPosition[] } = {};
    lockerContent.forEach((locker) => {
      let sid = locker.extraFields?.sid;
      let isActive = locker.isActive;
      if (sid && isActive) {
        if (!groupedLockers[sid]) {
          groupedLockers[sid] = [];
        }
        groupedLockers[sid].push(locker);
      }
    });
    Object.values(groupedLockers).forEach((locker) => {
      if (locker.length === PRIMARY_COLUMN_COMPARTMENT_LENGTH) {
        locker.splice(
          PRIMARY_MASTER_COMPARTMENT_INDEX,
          0,
          PRIMARY_MASTER_COMPARTMENT
        );
      }
    });

    return Object.values(groupedLockers).map((lockers, index) => (
      <LockerColumn
        key={index}
        lockerColumn={lockers}
        onSelectCompartment={(compartment) => {
          if (!testing) {
            console.log(compartment);

            setCurrentStep(0);
            updateCurrentLockerPosition(compartment);
            setInstructions(
              languageMap.agent_locker_fix.pre_test_instructions[language]
            );
          }
        }}
        currentCompartment={{} as LockerPosition}
        showStatus
      />
    ));
  };
  const renderLegend = () => {
    return (
      <div className="locker-fix-legend-container">
        <div className="locker-fix-legend-item">
          <div className="locker-fix-legend-indicator free" />
          <p className="locker-fix-legend-text">
            {languageMap.agent_locker_fix.free[language]}
          </p>
        </div>
        <div className="locker-fix-legend-item">
          <div className="locker-fix-legend-indicator occupied" />
          <p className="locker-fix-legend-text">
            {languageMap.agent_locker_fix.occupied[language]}
          </p>
        </div>
        <div className="locker-fix-legend-item">
          <div className="locker-fix-legend-indicator damaged" />
          <p className="locker-fix-legend-text">
            {languageMap.agent_locker_fix.damaged[language]}
          </p>
        </div>
      </div>
    );
  };
  const renderContent = () => {
    return (
      <div className="locker-fix-content-container">
        {renderLegend()}
        <div className="locker-fix-locker-container">{renderLocker()}</div>
      </div>
    );
  };
  const renderAWB = () => {
    if (currentLockerPosition?.packageCandidate?.awbNumber) {
      return (
        <div className="locker-fix-side-nav-item-content">
          <p className="locker-fix-side-nav-text-title">
            {languageMap.agent_locker_fix.awb[language]}
          </p>
          <p className="locker-fix-side-nav-text">
            {currentLockerPosition?.packageCandidate?.awbNumber}
          </p>
        </div>
      );
    }
  };
  const renderPieceStatus = () => {
    if (currentLockerPosition.id !== undefined) {
      return (
        <div className="locker-fix-side-nav-item small wrapped">
          <div className="locker-fix-side-nav-item-content">
            <p className="locker-fix-side-nav-text-title">
              {languageMap.agent_locker_fix.compartment[language]}
            </p>
            <p className="locker-fix-side-nav-text">
              {currentLockerPosition?.row}
              {currentLockerPosition?.column ?? '-'}
            </p>
          </div>
          <div className="locker-fix-side-nav-item-content">
            <p className="locker-fix-side-nav-text-title">
              {languageMap.agent_locker_fix.state[language]}
            </p>
            <p className="locker-fix-side-nav-text">
              {currentLockerPosition?.lockerPositionStatus?.name ?? '-'}
            </p>
          </div>
          {renderAWB()}
        </div>
      );
    }
    return <div className="locker-fix-side-nav-item small wrapped" />;
  };
  const renderLoader = () => {
    if (testing) {
      return (
        <div className="spinner-container">
          <Spinner className="spinner" animation="grow" variant="danger" />
        </div>
      );
    }
  };
  const renderProgressBar = (currentStep: number) => {
    const steps = ['Step 1', 'Step 2', 'Step 3'];
    if (testing || currentStep !== 0) {
      return (
        <div className="locker-fix-progress-bar">
          {steps.map((step, index) => (
            <div key={index} className="locker-fix-progress-bar-step">
              {index <= currentStep ? (
                <FaCheckCircle className="locker-fix-check-icon" />
              ) : testing ? (
                <FaCircle className="locker-fix-progress-icon" />
              ) : (
                <MdCancel className="locker-fix-error-icon" />
              )}
              {index < currentStep ? (
                <div className="locker-fix-progress-bar-progress checked" />
              ) : index < steps.length - 1 ? (
                <div className="locker-fix-progress-bar-progress" />
              ) : null}
            </div>
          ))}
        </div>
      );
    }
  };
  const renderTesterContainer = () => {
    if (currentLockerPosition.id === undefined) {
      return <p className="locker-fix-side-nav-instructions">{instructions}</p>;
    }
    return (
      <div className="locker-fix-side-nav-item large center-items">
        <p className="locker-fix-side-nav-instructions">{instructions}</p>
        <NavigationButton
          buttonContent={languageMap.agent_locker_fix.test_button[language]}
          buttonClass="navigation-button confirm-button"
          disabled={testing}
          onClickButton={() => {
            testCompartment();
          }}
        />
        <div className="locker-fix-side-nav-loader">{renderLoader()}</div>
        {renderProgressBar(currentStep)}
      </div>
    );
  };
  const renderSideNav = () => {
    return (
      <div className="locker-fix-side-nav-container">
        {renderPieceStatus()}
        {renderTesterContainer()}
        <div className="locker-fix-side-nav-item small"></div>
      </div>
    );
  };
  return (
    <div id="agentLockerFix" className="main-container">
      <TopNav dataCyPrefix="scan-awb" />
      <div className="content-container">
        <div className="main-content-container">
          <TopNavToastMessage
            toastText={feedback.message}
            toastType={feedback.type}
            resetMessage={() =>
              setFeedback({
                message: '',
                type: ToastType.SUCCESS,
              })
            }
          />
          <div className="title-content-container">
            <p className="header nopadding nomargin">
              {languageMap.agent_locker_fix.title[language]}
            </p>
          </div>
          <div className="data-content-container">{renderContent()}</div>
          <div className="footer-container">
            <div className="footer-button-container">
              <NavigationButton
                buttonContent={
                  languageMap.agent_locker_fix.cancel_button[language]
                }
                buttonClass="navigation-button cancel-button"
                onClickButton={() => {
                  history.replace(
                    `/${navigationPages.AGENT_PROCESS_SELECTION}`
                  );
                }}
              />
            </div>
            <div className="footer-button-container">
              {/* <NavigationButton
                buttonContent="TEST DAMAGE"
                buttonClass="navigation-button confirm-button"
                onClickButton={() => {
                  apiTestDamageCompartment();
                }}
              /> */}
            </div>
          </div>
        </div>
        {renderSideNav()}
      </div>
    </div>
  );
};

export { AgentLockerFix };
